import { useCallback, useEffect, useMemo, useState } from "react";

import { type ChartDataset } from "chart.js";
import { orderBy, round } from "lodash";
import { type ChartJSOrUndefined } from "react-chartjs-2/dist/types";

import { LoadingSlideAtom } from "@components/title-slide-view";
import { useUser } from "@components/user-context";
import {
  KpiType,
  type ModelMetadataKpiFormula,
} from "@features/mmm/components/explore-budgets/client-setting/types";
import { useAdoptedBudgetMix } from "@features/mmm/hooks/use-adopted-budget-mix";
import { formatCurrencyApprox } from "@features/mmm/utils/formatters";
import { lighten } from "@mantine/core";
import { MetricFormatters } from "@north-beam/nb-client-side";
import { ModelInsightsTrackedComponent } from "@pages/mmm/amplitude-tracking/components";
import { MMMTab } from "@pages/mmm/amplitude-tracking/mmm-event";
import { useTrackMMMEvent } from "@pages/mmm/amplitude-tracking/use-track-mmm-event";

import { curateHistoricalDataset } from "../../model-fit-chart/utils/export-to-csv";

import { bucketData } from "./bucket-data";
import { getChartOptions } from "./chart-options";
import {
  OBSERVED_SPEND_COLOR,
  OBSERVED_SPEND_ID,
  REVENUE_COLOR,
  REVENUE_ID,
  ROAS_COLOR,
  ROAS_ID,
  type DataColumn,
  ROAS_BORDER_DASH,
} from "./const";
import { Legend } from "./legend";
import { type BarOrLine, MixedChart } from "./mixed-chart";
import { plugins } from "./plugins";
import { setChartData } from "./plugins/chart-data-manager-plugin";
import { setYAxesTitles } from "./plugins/y-scale-titles-plugin";

type HistoricalContribution = {
  date: string;
  kpiData: Record<string, number>;
  spend: number;
};

export type HistoricalContributionsData = Record<
  string,
  HistoricalContribution[]
>;

const BUCKET_COUNT = 20;

interface Props {
  columnName: string;
  costCurvesAllTimeData: any;
  costCurvesAllTimeByQuantileData: any;
  selectedKpi: ModelMetadataKpiFormula;
  column: DataColumn;
  historicalContributionsData: HistoricalContributionsData;
  weeklySpends: number[];
  dateRange: string;
}

const allPairs: { [key: string]: number } = {
  "0.16": 0.8,
  "0.24": 0.6,
  "0.32": 0.4,
  "0.4": 0.2,
  "0.48": 0.0,
  "0.56": 0.2,
  "0.64": 0.4,
  "0.72": 0.6,
  "0.84": 0.8,
};

export const IncrementalSpendChart = ({
  columnName,
  costCurvesAllTimeData,
  costCurvesAllTimeByQuantileData,
  historicalContributionsData,
  selectedKpi,
  weeklySpends,
  dateRange,
}: Props) => {
  const trackMMMEvent = useTrackMMMEvent(MMMTab.MODEL_INSIGHTS);
  const { user } = useUser();
  const { adoptedBudgetMix } = useAdoptedBudgetMix();

  const [chartRef, setChartRef] = useState<ChartJSOrUndefined<any>>();

  const setChartInstance = useCallback(
    (instance: ChartJSOrUndefined | null) => {
      if (instance) {
        setChartRef(instance);
      }
    },
    [],
  );

  const [datasetVisibility, setDatasetVisibility] = useState<
    Record<string, boolean>
  >({
    [REVENUE_ID]: true,
    [ROAS_ID]: false,
    [OBSERVED_SPEND_ID]: true,
  });

  const isRevenue = selectedKpi.kpi_type === KpiType.REVENUE;
  const relevantHistoricalData = useMemo(() => {
    const rows = historicalContributionsData[columnName];
    return curateHistoricalDataset(rows, dateRange);
  }, [historicalContributionsData, dateRange, columnName]);

  const trueMedianSpendForHistoricalPeriod = useMemo(() => {
    const allSpends = relevantHistoricalData.map(
      (historicalDatum) => historicalDatum.spend,
    );
    allSpends.sort((a, b) => a - b);
    if (allSpends.length % 2 === 0) {
      const idx1 = allSpends.length / 2;
      const idx2 = idx1 - 1;
      return (allSpends[idx1] + allSpends[idx2]) / 2;
    } else {
      return allSpends[Math.floor(allSpends.length / 2)];
    }
  }, [relevantHistoricalData]);

  const { data, histogramLabels, getBucketFromSpendValue } = useMemo(() => {
    const rows = costCurvesAllTimeData[columnName] ?? [];
    const quantileData = costCurvesAllTimeByQuantileData[columnName] ?? {};

    const quantiles = orderBy(Object.keys(quantileData), "asc");

    const labels: number[] = [];
    const mainData: number[] = [];
    const quantileDatasets: { [quantile: string]: number[] } = {};
    const quantileDerivateDatasets: { [quantile: string]: number[] } = {};

    const derivativeData: number[] = [];

    rows.forEach((row: any, index: number) => {
      const value = row.kpiData[selectedKpi.kpi_name];
      labels.push(row.spend);
      mainData.push(value);

      derivativeData.push(isRevenue ? value / row.spend : row.spend / value);

      // go through each quantile
      quantiles.forEach((quantile) => {
        const quantileDataset = quantileDatasets[quantile];
        if (!quantileDataset) {
          quantileDatasets[quantile] = [];
        }
        if (!quantileDerivateDatasets[quantile]) {
          quantileDerivateDatasets[quantile] = [];
        }

        const quantileDataPoint =
          quantileData[quantile][index].kpiData[selectedKpi.kpi_name];
        quantileDatasets[quantile].push(quantileDataPoint);
        quantileDerivateDatasets[quantile].push(
          isRevenue
            ? quantileDataPoint / row.spend
            : row.spend / quantileDataPoint,
        );
      });
    });

    const roundedWeeklySpends = weeklySpends.map((spend) => round(spend, 2));
    const minSpend = round(labels[0], 2);
    const maxSpend = round(labels[labels.length - 1], 2);
    const spendRange = round(maxSpend - minSpend, 2);
    const bucketWidth = round(spendRange / BUCKET_COUNT, 2);
    const bucketedData = bucketData(
      roundedWeeklySpends,
      minSpend,
      bucketWidth,
      BUCKET_COUNT,
    );

    const histogramData = bucketedData.map(
      (histData) => histData.values.length,
    );
    const histogramLabels = bucketedData.map(
      (histData) =>
        `${MetricFormatters.currency(
          histData.min,
          user.currencies.currentCurrency,
          { precision: "approximate" },
        )} - ${MetricFormatters.currency(
          histData.max,
          user.currencies.currentCurrency,
          { precision: "approximate" },
        )}`,
    );

    const getBucketFromSpendValue = (spend: number) => {
      const labelIndex = bucketedData.findIndex(
        (bucket) => spend >= bucket.min && spend <= bucket.max,
      );
      return labelIndex !== -1 ? histogramLabels[labelIndex] : "Unknown";
    };

    const datasets: ChartDataset<BarOrLine>[] = [
      {
        animation: false,
        yAxisID: ROAS_ID,
        xAxisID: "x",
        label: isRevenue ? "Incremental ROAS" : "Incremental CAC",
        type: "line",
        backgroundColor: ROAS_COLOR,
        borderColor: ROAS_COLOR,
        borderDash: ROAS_BORDER_DASH,
        tension: 0.4,
        pointRadius: 0,
        order: 1,
        data: derivativeData,
      } as ChartDataset<"line", number[]>,
      {
        animation: false,
        yAxisID: REVENUE_ID,
        xAxisID: "x",
        label: selectedKpi.friendly_name,
        type: "line",
        backgroundColor: REVENUE_COLOR,
        borderColor: REVENUE_COLOR,
        pointHitRadius: 3,
        tension: 0.4,
        pointRadius: 0,
        data: mainData,
      } as ChartDataset<"line", number[]>,
      {
        animation: false,
        type: "bar",
        label: OBSERVED_SPEND_ID,
        data: histogramData,
        xAxisID: "histogramX",
        yAxisID: OBSERVED_SPEND_ID,
        categoryPercentage: 1,
        barPercentage: 1,
        backgroundColor: OBSERVED_SPEND_COLOR,
        borderColor: OBSERVED_SPEND_COLOR,
      } as ChartDataset<"bar", number[]>,
      ...Object.keys(quantileDatasets).map((quantile) => {
        const ordering = allPairs[quantile] * 10;

        return {
          order: ordering + 5,
          yAxisID: REVENUE_ID,
          xAxisID: "x",
          id: quantile,
          label: `${selectedKpi.friendly_name} - ${quantile}`,
          type: "line",
          backgroundColor: lighten("#A9B3FF", allPairs[quantile]),
          fill: "1",
          pointHitRadius: 0,
          pointHoverRadius: 0,
          pointRadius: 0,
          borderWidth: 0,
          animation: false,
          data: quantileDatasets[quantile],
        } as ChartDataset<"line", number[]>;
      }),
    ];

    return {
      data: { labels, datasets },
      histogramLabels,
      getBucketFromSpendValue,
    };
  }, [
    costCurvesAllTimeData,
    columnName,
    costCurvesAllTimeByQuantileData,
    weeklySpends,
    isRevenue,
    selectedKpi.friendly_name,
    selectedKpi.kpi_name,
    user.currencies.currentCurrency,
  ]);

  const medianSpendIndex = useMemo(
    () =>
      findClosestIndexInArrayForValue(
        data.labels,
        trueMedianSpendForHistoricalPeriod,
      ),
    [data.labels, trueMedianSpendForHistoricalPeriod],
  );

  const medianSpendValue = formatCurrencyApprox(
    trueMedianSpendForHistoricalPeriod,
    user.currencies.currentCurrency,
  );

  const averageAdoptedBudgetMixSpendForChannel = useMemo(() => {
    if (!adoptedBudgetMix) {
      return null;
    }

    const totalWeeks = adoptedBudgetMix.data.weekly_data.length;
    let totalSpendForChannel = 0;
    adoptedBudgetMix.data.aggregated_data.spend_data.forEach((spendData) => {
      if (spendData.data_column_name === columnName) {
        totalSpendForChannel += spendData.spend;
      }
    });

    return totalSpendForChannel / totalWeeks;
  }, [adoptedBudgetMix, columnName]);

  const averageAdoptedMixSpendIndex = useMemo(() => {
    if (averageAdoptedBudgetMixSpendForChannel === null) {
      return null;
    }
    return findClosestIndexInArrayForValue(
      data.labels,
      averageAdoptedBudgetMixSpendForChannel,
    );
  }, [data.labels, averageAdoptedBudgetMixSpendForChannel]);

  const averageAdoptedMixValue =
    averageAdoptedBudgetMixSpendForChannel ||
    averageAdoptedBudgetMixSpendForChannel === 0
      ? formatCurrencyApprox(
          averageAdoptedBudgetMixSpendForChannel,
          user.currencies.currentCurrency,
        )
      : "";

  const hasDifferentXAxis = useMemo(
    () =>
      datasetVisibility[OBSERVED_SPEND_ID] &&
      (datasetVisibility[ROAS_ID] || datasetVisibility[REVENUE_ID]),
    [datasetVisibility],
  );

  useEffect(() => {
    if (chartRef) {
      setChartData(chartRef, {
        data,
        datasetVisibility,
        getBucketFromSpendValue,
      });
      setYAxesTitles({
        chart: chartRef,
        naming: {
          [OBSERVED_SPEND_ID]: "Observed Spend (Weeks)",
          [ROAS_ID]: isRevenue ? "Incremental ROAS" : "Incremental CAC",
          [REVENUE_ID]: selectedKpi.friendly_name,
        },
      });
    }
  }, [
    data,
    datasetVisibility,
    getBucketFromSpendValue,
    isRevenue,
    selectedKpi.friendly_name,
    chartRef,
    dateRange,
  ]);

  const chartOptions = useMemo(
    () =>
      getChartOptions({
        user,
        selectedKpi,
        histogramLabels,
        hasDifferentXAxis,
        dateRange,
        annotationArgs: {
          medianSpendIndex,
          medianSpendValue,
          averageAdoptedMixSpendIndex,
          averageAdoptedMixValue,
        },
      }),
    [
      user,
      selectedKpi,
      histogramLabels,
      hasDifferentXAxis,
      dateRange,
      medianSpendIndex,
      medianSpendValue,
      averageAdoptedMixSpendIndex,
      averageAdoptedMixValue,
    ],
  );

  if (
    !costCurvesAllTimeData[columnName] ||
    !historicalContributionsData[columnName]
  ) {
    return (
      <div className="flex-center h-[457px]">
        <LoadingSlideAtom />
      </div>
    );
  }

  return (
    <div>
      <div className="font-bold text-lg self-start flex items-center space-x-4 px-5 pb-3">
        <div>Incremental Spend vs {selectedKpi.friendly_name}</div>
        <div className="rounded-full py-1 px-3.5 bg-pluto text-white text-xs">
          ALL TIME
        </div>
      </div>
      <Legend
        selectedKpi={selectedKpi}
        datasetVisibility={datasetVisibility}
        setDatasetVisibility={(value) => {
          trackMMMEvent({
            component: ModelInsightsTrackedComponent.INCREMENTAL_SPEND_CHART,
            action: "onChange",
            actionInfo: "Change dataset visibility",
          });

          setDatasetVisibility(value);
        }}
      />
      <div className="relative">
        <MixedChart
          ref={setChartInstance}
          options={chartOptions}
          data={data}
          height={80}
          plugins={plugins}
        />
        <div className="text-xs text-center font-semibold">SPEND (WEEKLY)</div>
      </div>
    </div>
  );
};

export const findClosestIndexInArrayForValue = (
  values: number[],
  targetValue: number,
) => {
  const smallestGteIndex = values.findIndex((label) => label >= targetValue);
  if (smallestGteIndex < 0) {
    return values.length - 1;
  }
  if (smallestGteIndex === 0) {
    return 0;
  }
  const smallestGteValue = values[smallestGteIndex];

  if (smallestGteValue === targetValue) {
    return smallestGteIndex;
  }

  const largestLtIndex = smallestGteIndex - 1;
  const largestLtValue = values[largestLtIndex];

  if (
    Math.abs(smallestGteValue - targetValue) <
    Math.abs(targetValue - largestLtValue)
  ) {
    return smallestGteIndex;
  } else {
    return largestLtIndex;
  }
};
